import type { Failure, FailureCode, FieldCode } from "@/modules/shared/failure";

/**
 * The one way this app talks to the API Worker (R22 T10).
 *
 * **Every call comes back as a `Result`, never as a thrown exception**, which
 * is the same discipline the command cores follow on the server: a refusal is
 * an ordinary outcome a screen renders, and only a genuine fault is
 * exceptional. A component that has to remember a try/catch will eventually
 * forget one, and the forgetting shows up as a blank screen.
 *
 * **The client branches on `code`, never on the status number.** One status
 * covers many distinct refusals (a 409 is `slot_taken` and `booking_expired`
 * and six others), so the status is for proxies and logs while the code is for
 * the screen. This is the rule `src/api/_lib/respond.ts` states from the other
 * side of the wire.
 *
 * **The sentences do not live here.** Each module's `commands` folder holds a
 * pure, unit-tested copy map (`authCopy.ts` and its siblings), and that map is
 * the web's copy, so this client hands back a `Failure` and lets the screen
 * render it through the very map the Next host used. That is what keeps a
 * wording change from becoming a two-frontend change.
 */

export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly failure: Failure };

/** The envelope `respond.ts` writes: `{ data }` or `{ error: { code, ... } }`. */
interface ErrorEnvelope {
  error?: {
    code?: unknown;
    field?: unknown;
    fieldErrors?: unknown;
    retryAfterSeconds?: unknown;
  };
  data?: unknown;
}

/**
 * Turn a response body into a Result, without trusting any of it.
 *
 * Pure and exported so it can be unit-tested against the shapes a broken or
 * intercepted response actually takes: an HTML error page from a proxy, an
 * empty body, a `code` that is a number. A parser that assumes the happy shape
 * turns a bad gateway into a type error three components away.
 */
export function parseEnvelope<T>(status: number, body: unknown): ApiResult<T> {
  const envelope = (body ?? {}) as ErrorEnvelope;

  if (status >= 200 && status < 300) {
    return { ok: true, value: envelope.data as T };
  }

  const error = envelope.error ?? {};
  const code = typeof error.code === "string" ? error.code : "system_error";

  const failure: {
    code: FailureCode;
    field?: string;
    fieldErrors?: Record<string, FieldCode>;
    retryAfterSeconds?: number;
  } = { code: code as FailureCode };

  if (typeof error.field === "string") failure.field = error.field;

  if (error.fieldErrors && typeof error.fieldErrors === "object") {
    const fieldErrors: Record<string, FieldCode> = {};
    for (const [field, value] of Object.entries(
      error.fieldErrors as Record<string, unknown>,
    )) {
      if (typeof value === "string") fieldErrors[field] = value as FieldCode;
    }
    if (Object.keys(fieldErrors).length > 0) failure.fieldErrors = fieldErrors;
  }

  if (typeof error.retryAfterSeconds === "number") {
    failure.retryAfterSeconds = error.retryAfterSeconds;
  }

  return { ok: false, failure: failure as Failure };
}

/** Where the bearer token comes from. Injected so this file has no auth import. */
export type TokenSource = () => Promise<string | null>;

let tokenSource: TokenSource = async () => null;

/**
 * Wire up the token source once, at start-up.
 *
 * The alternative was importing the supabase client here, which would have made
 * every test of this file boot an auth library to prove that a 409 body parses.
 */
export function configureTokenSource(source: TokenSource): void {
  tokenSource = source;
}

/** What a caller passes; `path` is relative to the web group's `/v1`. */
export interface RequestOptions {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Skip the Authorization header on the surfaces a guest legitimately uses. */
  readonly anonymous?: boolean;
}

const BASE = "/api/web/v1";

export async function call<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = { Accept: "application/json" };

  if (!options.anonymous) {
    const token = await tokenSource();
    if (token) headers.Authorization = "Bearer " + token;
  }
  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(BASE + path, {
      method: options.method ?? "GET",
      headers,
      body:
        options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    // Offline, DNS, a cancelled navigation. Nothing the server said, so it
    // cannot be a coded refusal: `system_error` is the honest answer, and the
    // screen's banner copy for it already says "please try again".
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    return { ok: false, failure: { code: "system_error" } as Failure };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // A 204, or a proxy's HTML. parseEnvelope handles both from here.
  }

  return parseEnvelope<T>(response.status, body);
}

export const get = <T,>(path: string, options?: RequestOptions) =>
  call<T>(path, { ...options, method: "GET" });

export const post = <T,>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
) => call<T>(path, { ...options, method: "POST", body });

export const put = <T,>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
) => call<T>(path, { ...options, method: "PUT", body });

export const del = <T,>(path: string, options?: RequestOptions) =>
  call<T>(path, { ...options, method: "DELETE" });
